import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, DatevKopplung, DatevKopplungEingabe } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand }
  from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { alsFeldwert, alsZahl, eingabeUnlesbar, KEINE_ZAHL, meldungAus, Meldung }
  from "./gemeinsam.js";
import "./datev.css";
import { KiKnopf } from "../../components/meHuellen.js";

/**
 * DER BLOCK „DATEV" in Verwaltung → Unternehmen (Spec, „Stammdaten je Unternehmen").
 *
 * Nur ADMIN — die ganze Verwaltung ist es. Er zeigt IMMER alle Felder, auch ohne
 * gespeicherte Zeile: Der Server antwortet dann mit den Vorgaben und
 * `eingerichtet: false`, und der erste Speichervorgang legt die Zeile an (Aufgabe 4).
 *
 * **Ein eigenes Formular mit eigenem Speichern-Knopf**, nicht Teil der Stammdatenmaske
 * darüber: Die Kopplung ist ein EIGENER Satz mit eigenem Weg (`PUT …/datev`), eigener
 * Prüfung und eigenen Feldfehlern. Sie an den Speichern-Knopf der Stammdaten zu hängen
 * hiesse, zwei Schreibwege hinter einem Knopf zu verstecken — und bei einem Fehlschlag
 * des zweiten stünde der erste schon in der Datenbank.
 *
 * **Bearbeitet wird im STRIKTEN Modalpanel** (Hausregel „Modal-Bearbeitung" — „Kopplung
 * pflegen" steht dort ausdrücklich): Das Detail LIEST die Felder, „Ändern" öffnet das
 * Formular. Dieselbe Bauart wie die zwölf Stammdatenfelder darüber in `unternehmen.tsx`.
 *
 * **Die Schalterzeile „Kopplung eingeschaltet" steht GANZ OBEN im Panel** und trägt den
 * Satz, der die wichtigste Auskunft dieses Blocks ist: „Ohne eingeschaltete Kopplung
 * laufen Rechnungen wie bisher; es entsteht kein Übergabeposten."
 *
 * Feldfehler kommen als 422 `DATEV_UNGUELTIG` mit `fields[]` und werden AN DAS FELD
 * geschrieben (Muster `KundeBeziehungen`); ein Feld, das diese Maske nicht zeigt, landet
 * im Meldungsband — unsichtbar darf kein Befund bleiben.
 *
 * **ME-Bausteine (ME-Etappe 3, Aufgabe 7):** Felder als `Feld` im `Formular`
 * (`beschriftung="oben"`), Kontenrahmen als `Auswahl`, „Kopplung eingeschaltet" als
 * `Schalter`, Speichern über `useFormularZustand` (Sperre, Feldfehler mit Sprung zum
 * ersten, Sammelmeldung mit deutschen Beschriftungen), Quittung als `Hinweis art="ok"`.
 *
 * Eigener CSS-Block `dtv` (views/verwaltung/datev.css) — dieselbe Wortwahl wie im Bereich
 * Schnittstellen → DATEV, aber ein EIGENES Stylesheet (Hausregel „CSS-Blocknamen je
 * Ansicht getrennt").
 */

export type DatevBlockClient = Pick<ApiClient, "getDatevKopplung" | "setDatevKopplung">;

export type DatevBlockProps = {
  client: DatevBlockClient;
  /** Das Unternehmen, dessen Kopplung hier steht. Ein Wechsel setzt ALLES zurück und lädt
   *  neu — derselbe Kontextwechsel wie im Bereich Schnittstellen → DATEV. */
  companyId: string;
};

/** Der Entwurf im Bearbeiten-Panel — Zahlenfelder als TEXT (ein Eingabefeld kennt kein
 *  `number | null`), zurückverwandelt beim Speichern über `alsZahl` (Muster
 *  `Nummernkreise.tsx`). */
type Entwurf = {
  consultantNumber: string; clientNumber: string;
  chartOfAccounts: "SKR03" | "SKR04";
  accountLength: string; fiscalYearStartMonth: string;
  revenueAccountStandard: string; revenueAccountReduced: string;
  revenueAccountExempt: string; revenueAccountIntraEu: string;
  taxKeyStandard: string; taxKeyReduced: string;
  taxKeyExempt: string; taxKeyIntraEu: string;
  debtorRangeFrom: string; debtorRangeTo: string;
  active: boolean;
};

function entwurfAus(k: DatevKopplung): Entwurf {
  return {
    consultantNumber: alsFeldwert(k.consultantNumber),
    clientNumber: alsFeldwert(k.clientNumber),
    chartOfAccounts: k.chartOfAccounts,
    accountLength: String(k.accountLength),
    fiscalYearStartMonth: String(k.fiscalYearStartMonth),
    revenueAccountStandard: k.revenueAccountStandard,
    revenueAccountReduced: k.revenueAccountReduced,
    revenueAccountExempt: k.revenueAccountExempt,
    revenueAccountIntraEu: k.revenueAccountIntraEu,
    taxKeyStandard: String(k.taxKeyStandard),
    taxKeyReduced: String(k.taxKeyReduced),
    taxKeyExempt: String(k.taxKeyExempt),
    taxKeyIntraEu: String(k.taxKeyIntraEu),
    debtorRangeFrom: String(k.debtorRangeFrom),
    debtorRangeTo: String(k.debtorRangeTo),
    active: k.active,
  };
}

/** Nur die Zahlenfelder — für `alsZahl` und die 422-Feldnamen. Die drei Textfelder
 *  (`chartOfAccounts`, vier `revenueAccount*`) tippt man unverändert durch. */
const ZAHLENFELDER = [
  "consultantNumber", "clientNumber", "accountLength", "fiscalYearStartMonth",
  "taxKeyStandard", "taxKeyReduced", "taxKeyExempt", "taxKeyIntraEu",
  "debtorRangeFrom", "debtorRangeTo",
] as const;
type Zahlenfeld = (typeof ZAHLENFELDER)[number];

/** Alle Feldnamen, die eine 422 `DATEV_UNGUELTIG` nennen kann UND die diese Maske zeigt. */
type FeldName = Zahlenfeld | "chartOfAccounts" | "revenueAccountStandard"
  | "revenueAccountReduced" | "revenueAccountExempt" | "revenueAccountIntraEu";

/** Die Felder, die diese Maske zeigt — eine 422 zu einem anderen Feld landet in der
 *  Sammelmeldung (`useFormularZustand`, `bekannteFelder`). */
const BEKANNTE_FELDER: readonly string[] = [
  ...ZAHLENFELDER, "chartOfAccounts", "revenueAccountStandard", "revenueAccountReduced",
  "revenueAccountExempt", "revenueAccountIntraEu", "active",
];

/** Berater-/Mandantennummer dürfen leer sein (NULL), alle übrigen Zahlenfelder sind
 *  Pflicht — der Server weist ein fehlendes Pflichtfeld ohnehin mit 422 ab, aber ein
 *  leeres Sachkonten-/BU-Feld hier abzuschicken behauptete „0", was niemand gemeint hat. */
const PFLICHTFELDER: ReadonlySet<FeldName> = new Set<FeldName>(["accountLength",
  "fiscalYearStartMonth", "taxKeyStandard", "taxKeyReduced", "taxKeyExempt",
  "taxKeyIntraEu", "debtorRangeFrom", "debtorRangeTo",
  // Die vier Erlöskonten verlangt der Server ebenso (genau Sachkontenlänge Ziffern,
  // domain/datevRules.ts) — der Stern sagt es vorher (Review A7). Sie sind Text, keine
  // Zahlenfelder: Die Leer-Prüfung unten (`pflichtLeer`) gilt ihnen nicht, das tut der Server.
  "revenueAccountStandard", "revenueAccountReduced", "revenueAccountExempt",
  "revenueAccountIntraEu"]);

const FELD_LABEL: Record<FeldName, string> = {
  consultantNumber: "Beraternummer", clientNumber: "Mandantennummer",
  chartOfAccounts: "Kontenrahmen", accountLength: "Sachkontenlänge",
  fiscalYearStartMonth: "Wirtschaftsjahr-Beginn",
  revenueAccountStandard: "Erlöskonto Standard",
  revenueAccountReduced: "Erlöskonto ermässigt",
  revenueAccountExempt: "Erlöskonto steuerfrei",
  revenueAccountIntraEu: "Erlöskonto innergemeinschaftlich",
  taxKeyStandard: "BU-Schlüssel Standard", taxKeyReduced: "BU-Schlüssel ermässigt",
  taxKeyExempt: "BU-Schlüssel steuerfrei", taxKeyIntraEu: "BU-Schlüssel innergemeinschaftlich",
  debtorRangeFrom: "Debitorenkreis von", debtorRangeTo: "Debitorenkreis bis",
};

export function DatevBlock(p: DatevBlockProps): ReactElement {
  const [kopplung, setKopplung] = useState<DatevKopplung | null | undefined>(undefined);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [erfolg, setErfolg] = useState(false);

  const lauf = useRef(0);
  const companyId = p.companyId;
  const getDatevKopplung = p.client.getDatevKopplung;

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 7): `laeuft` sperrt Felder und Fuss, eine 422
   * (auch die eigene „keine Zahl"/„darf nicht leer sein") hängt am Feld und der Fokus
   * springt dorthin; ein Befund an einem nicht gezeigten Feld steht deutsch beschriftet in
   * der Sammelmeldung. Die Kopplung trägt keine Version — eine 409 gibt es auf diesem Weg
   * nicht; „Neu laden" liest trotzdem den Serverstand, falls sie je käme.
   */
  const formular = useFormularZustand<boolean>({
    speichern: async () => {
      const e = entwurf;
      if (e === null) throw new Error("Keine Kopplung offen.");
      const meiner = lauf.current;
      const eingabe = eingabeAus(e);
      let neu: DatevKopplung;
      try {
        neu = await p.client.setDatevKopplung(companyId, eingabe);
      } catch (fehler) {
        // Auch der FEHLSCHLAG gehört dem alten Unternehmen (Review A7): Nach einem Wechsel
        // mitten im Speichern stünde seine Meldung sonst im Block des neuen.
        if (meiner !== lauf.current) return false;
        if (fehler instanceof ApiError) throw fehler;
        throw new Error("Unerwarteter Fehler beim Speichern der Kopplung.");
      }
      // Ein Unternehmenswechsel mitten im Speichern: Die Antwort gehört dem alten.
      if (meiner !== lauf.current) return false;
      setKopplung(neu);
      return true;
    },
    neuLaden: async () => {
      const k = await getDatevKopplung(companyId);
      setKopplung(k);
      setEntwurf((alt) => (alt === null ? alt : entwurfAus(k)));
    },
    bekannteFelder: BEKANNTE_FELDER,
    beschriftungen: { ...FELD_LABEL, active: "Kopplung eingeschaltet" },
  });
  const feldFehler = formular.feldFehler as Partial<Record<FeldName | "active", string>>;
  const speichert = formular.laeuft;

  /* KEIN ZUSTAND ÜBERLEBT DEN UNTERNEHMENSWECHSEL (Hausregel). */
  useEffect(() => {
    const meiner = (lauf.current += 1);
    setKopplung(undefined);
    setLadeFehler(null);
    setEntwurf(null);
    formular.zuruecksetzen();
    setErfolg(false);
    // Als ASYNC FUNKTION und nicht als rohe Promise-Kette: Ein synchroner Fehlschlag des
    // Aufrufs selbst (etwa im Test ein fehlender `vi.fn`) landet damit ebenfalls im
    // `catch` statt als unbehandelte Ausnahme den ganzen Effekt zu sprengen.
    (async () => {
      try {
        const k = await getDatevKopplung(companyId);
        if (meiner === lauf.current) setKopplung(k);
      } catch (e) {
        if (meiner !== lauf.current) return;
        setKopplung(null);
        setLadeFehler(meldungAus(e, "Die DATEV-Kopplung liess sich nicht laden."));
      }
    })();
    return () => { lauf.current += 1; };
  }, [companyId, getDatevKopplung]);

  const geaendert = entwurf !== null && kopplung !== null && kopplung !== undefined
    && JSON.stringify(entwurf) !== JSON.stringify(entwurfAus(kopplung));
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    if (kopplung == null) return;
    formular.zuruecksetzen();
    setEntwurf(entwurfAus(kopplung));
    setErfolg(false);
  }

  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setEntwurf(null);
    formular.zuruecksetzen();
  }

  function feldWert(feld: keyof Entwurf, wert: string): void {
    setEntwurf((e) => (e === null ? e : { ...e, [feld]: wert }));
  }

  /** Der Rumpf für den Schreibweg — wirft die eigene 422 (dieselbe Gestalt wie ein Befund
   *  des Servers, `eingabeUnlesbar`), wenn ein Zahlenfeld keine Zahl ist oder ein
   *  Pflichtfeld leer. So hängt der Befund am Feld, und der Fokus springt dorthin. */
  function eingabeAus(e: Entwurf): DatevKopplungEingabe {
    const zahlen: Partial<Record<Zahlenfeld, number | null>> = {};
    const ungueltig: { field: FeldName; message: string }[] = [];
    for (const feld of ZAHLENFELDER) {
      const roh = alsZahl(e[feld]);
      if (roh === KEINE_ZAHL) {
        ungueltig.push({ field: feld, message: `${FELD_LABEL[feld]} ist keine Zahl.` });
        continue;
      }
      zahlen[feld] = roh;
    }
    if (ungueltig.length > 0) throw eingabeUnlesbar(ungueltig);
    const pflichtLeer = ZAHLENFELDER.filter((f) => PFLICHTFELDER.has(f) && zahlen[f] === null);
    if (pflichtLeer.length > 0) {
      throw eingabeUnlesbar(pflichtLeer.map((f) => (
        { field: f, message: `${FELD_LABEL[f]} darf nicht leer sein.` })));
    }
    return {
      consultantNumber: zahlen.consultantNumber ?? null,
      clientNumber: zahlen.clientNumber ?? null,
      chartOfAccounts: e.chartOfAccounts,
      accountLength: zahlen.accountLength!,
      fiscalYearStartMonth: zahlen.fiscalYearStartMonth!,
      revenueAccountStandard: e.revenueAccountStandard,
      revenueAccountReduced: e.revenueAccountReduced,
      revenueAccountExempt: e.revenueAccountExempt,
      revenueAccountIntraEu: e.revenueAccountIntraEu,
      taxKeyStandard: zahlen.taxKeyStandard!,
      taxKeyReduced: zahlen.taxKeyReduced!,
      taxKeyExempt: zahlen.taxKeyExempt!,
      taxKeyIntraEu: zahlen.taxKeyIntraEu!,
      debtorRangeFrom: zahlen.debtorRangeFrom!,
      debtorRangeTo: zahlen.debtorRangeTo!,
      active: e.active,
    };
  }

  async function speichern(): Promise<void> {
    if (entwurf === null) return;
    if (await formular.aktion() === true) {
      setEntwurf(null);
      setErfolg(true);
    }
  }

  function zeile(feld: FeldName, wert: string): ReactElement {
    return (
      <p className="dtv__eintrag-zusatz">
        {FELD_LABEL[feld]}: {wert.trim() === "" ? "nicht gepflegt" : wert}
      </p>
    );
  }

  /** Ein Textfeld des Panels — Zahlen bleiben TEXT (siehe `Entwurf`), geprüft wird beim
   *  Speichern. Pflicht nur, wo ein leerer Wert nie gemeint sein kann. */
  function textfeld(feld: Exclude<FeldName, "chartOfAccounts">, wert: string): ReactElement {
    return (
      <Feld key={feld} id={`dtv-${feld}`} label={FELD_LABEL[feld]} bearbeitbar wert={wert}
        pflicht={PFLICHTFELDER.has(feld)}
        fehler={feldFehler[feld]}
        onChange={(w) => feldWert(feld, w)} />
    );
  }

  return (
    <section className="dtv" aria-label="DATEV">
      <h3 className="dtv__untertitel">DATEV</h3>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {erfolg && entwurf === null && <Hinweis art="ok">Gespeichert.</Hinweis>}
      {formular.meldung !== null && entwurf === null && <Meldung text={formular.meldung} />}

      {kopplung === undefined ? (
        <p className="dtv__hinweis">Lädt …</p>
      ) : kopplung === null ? null : (
        <>
          <p className="dtv__eintrag-zusatz">
            Kopplung: {kopplung.active ? "eingeschaltet" : "ausgeschaltet"}
            {!kopplung.eingerichtet && " (noch nicht gespeichert)"}
          </p>
          {zeile("consultantNumber", alsFeldwert(kopplung.consultantNumber))}
          {zeile("clientNumber", alsFeldwert(kopplung.clientNumber))}
          {zeile("chartOfAccounts", kopplung.chartOfAccounts)}
          {zeile("accountLength", String(kopplung.accountLength))}
          {zeile("fiscalYearStartMonth", String(kopplung.fiscalYearStartMonth))}
          {zeile("revenueAccountStandard", kopplung.revenueAccountStandard)}
          {zeile("revenueAccountReduced", kopplung.revenueAccountReduced)}
          {zeile("revenueAccountExempt", kopplung.revenueAccountExempt)}
          {zeile("revenueAccountIntraEu", kopplung.revenueAccountIntraEu)}
          {zeile("taxKeyStandard", String(kopplung.taxKeyStandard))}
          {zeile("taxKeyReduced", String(kopplung.taxKeyReduced))}
          {zeile("taxKeyExempt", String(kopplung.taxKeyExempt))}
          {zeile("taxKeyIntraEu", String(kopplung.taxKeyIntraEu))}
          {zeile("debtorRangeFrom", String(kopplung.debtorRangeFrom))}
          {zeile("debtorRangeTo", String(kopplung.debtorRangeTo))}

          <div className="dtv__aktionen">
            {/* Der zugängliche Name endet wie an jedem anderen Änderungsknopf dieser
                Verwaltung auf „ändern" und nennt davor das Objekt (Muster
                `oeffneAenderungspanel`, e2e/support/verwaltung.ts) — ohne das eigene
                `aria-label` läse ein Bildschirmleser hier nur „Ändern" und wüsste nicht,
                wovon. */}
            <KiKnopf deaktiviert={entwurf !== null} name="DATEV-Kopplung ändern" onClick={oeffnen}>
              Ändern
            </KiKnopf>
          </div>
        </>
      )}

      {entwurf !== null && (
        <Modal titel="DATEV-Kopplung" breite="breit" onAbbrechen={abbrechen}
          abbrechenGesperrt={speichert}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}

          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? (
              <span>
                Die Kopplung wurde inzwischen geändert. Deine Eingaben stehen noch da —
                „Neu laden" übernimmt den aktuellen Stand.
              </span>
            ) : undefined}>
            {/* DIE SCHALTERZEILE STEHT GANZ OBEN (Kopfkommentar) — eine echte
                Ja/Nein-Angabe, deshalb `Schalter` (me-schalter) statt Kästchen. */}
            <Schalter id="dtv-active" beschriftung="Kopplung eingeschaltet" an={entwurf.active}
              {...(feldFehler.active !== undefined ? { fehler: feldFehler.active } : {})}
              onGeaendert={(d) => setEntwurf((e) => (e === null ? e
                : { ...e, active: d.an }))} />
            <p className="dtv__hinweis">
              Ohne eingeschaltete Kopplung laufen Rechnungen wie bisher; es entsteht kein
              Übergabeposten.
            </p>

            {textfeld("consultantNumber", entwurf.consultantNumber)}
            {textfeld("clientNumber", entwurf.clientNumber)}

            <Auswahl id="dtv-chartOfAccounts" name="dtv-chartOfAccounts"
              beschriftung="Kontenrahmen" wert={entwurf.chartOfAccounts}
              {...(feldFehler.chartOfAccounts !== undefined
                ? { fehler: feldFehler.chartOfAccounts } : {})}
              onGeaendert={(d) => setEntwurf((e) => (e === null ? e
                : { ...e, chartOfAccounts: d.wert as "SKR03" | "SKR04" }))}>
              <option value="SKR03">SKR03</option>
              <option value="SKR04">SKR04</option>
            </Auswahl>

            {textfeld("accountLength", entwurf.accountLength)}
            {textfeld("fiscalYearStartMonth", entwurf.fiscalYearStartMonth)}
            {textfeld("revenueAccountStandard", entwurf.revenueAccountStandard)}
            {textfeld("revenueAccountReduced", entwurf.revenueAccountReduced)}
            {textfeld("revenueAccountExempt", entwurf.revenueAccountExempt)}
            {textfeld("revenueAccountIntraEu", entwurf.revenueAccountIntraEu)}
            {textfeld("taxKeyStandard", entwurf.taxKeyStandard)}
            {textfeld("taxKeyReduced", entwurf.taxKeyReduced)}
            {textfeld("taxKeyExempt", entwurf.taxKeyExempt)}
            {textfeld("taxKeyIntraEu", entwurf.taxKeyIntraEu)}

            {/* Debitorenkreis — die Vorgaben sind ÄNDERBAR (Global Constraints): ohne
                Bereichsprüfung hier, geprüft wird auf dem Server. */}
            {textfeld("debtorRangeFrom", entwurf.debtorRangeFrom)}
            {textfeld("debtorRangeTo", entwurf.debtorRangeTo)}

            {/* Fixrunde 2, I2: Der Produktivbestand aus TOPIX (Migration 028, Teil 7)
                trägt SIEBENSTELLIGE Debitorennummern — ein Kreis, der das nicht
                abdeckt, lässt offene Posten leer bleiben und bestehende Nummern nicht
                ändern (`istImKreis`, `setzeDebitorennummer`). Der Hinweis steht HIER,
                direkt unter den beiden Feldern, statt nur in der Doku, weil er genau in
                dem Moment gebraucht wird, in dem jemand den Kreis einträgt. */}
            <p className="dtv__hinweis">
              Bestand aus TOPIX ist siebenstellig: Sachkontenlänge 6, Debitorenkreis
              1000000–6999999 eintragen, sonst bleiben offene Posten leer und bestehende
              Nummern lassen sich nicht ändern.
            </p>
          </Formular>

          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
